import { v4 as uuidv4 } from 'uuid';
import ApiService, { USERS_API_HOST } from './ApiService';
import { checkAppVersion } from '@/utils/app/checkAppVersion';
import { Capacitor } from '@capacitor/core';
import LZString from 'lz-string';

/**
 * @override
 */
class UsersService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: USERS_API_HOST });
    }

    /**
     * Get current user info
     * @param {Object} config Axios config
     * @return {Object} Response with list of users
     */
    async getMe(config) {
        const url = `/me?is_intervener=true&is_private_area=true${
            Capacitor.isNativePlatform() ? '&fromapp=true' : ''
        }`;

        return await this.get(url, config).then(async response => {
            const compressed = LZString.compressToUTF16(JSON.stringify(response.data));
            localStorage.setItem('PACOUS', compressed);

            await checkAppVersion(response.data.app_version_minimal);

            return response.data;
        });
    }

    getPrivateAreaSectionIfExists() {
        const compressed = localStorage.getItem('PACOUS');
        if (compressed) {
            try {
                const decompressed = LZString.decompressFromUTF16(compressed);
                const parsed = JSON.parse(decompressed);
                return parsed.private_area;
            } catch (e) {
                console.error('Error al descomprimir PACOUS:', e);
                return null;
            }
        }
        return null;
    }

    getIntervenerFromLocalStore() {
        const privateArea = this.getPrivateAreaSectionIfExists();
        if (privateArea && privateArea.intervener) {
            return privateArea.intervener;
        }
    }

    setIntervenerToLocalStore(intervener) {
        const compressed = localStorage.getItem('PACOUS');
        if (!compressed) return;

        try {
            const decompressed = LZString.decompressFromUTF16(compressed);
            const localStorageData = JSON.parse(decompressed);

            localStorageData.private_area.intervener = intervener;

            const newCompressed = LZString.compressToUTF16(JSON.stringify(localStorageData));
            localStorage.setItem('PACOUS', newCompressed);
        } catch (e) {
            console.error('Error al descomprimir o actualizar PACOUS:', e);
        }
    }

    login(credentials) {
        const headers = { 'X-Request-Id': uuidv4() };
        return this.post('/onboarding/login', credentials, headers);
    }

    /**
     * Get current user notifications
     * @param {Object} config Axios config
     * @return {Object} Response with list of notifications
     */
    getNotifications(config) {
        return this.get('/notifications', config);
    }

    /**
     * Retorna el registro de cobas_config
     * @param {Object} config Configuración de Axios
     * @param {String} keyName Palabra clave a retornar
     * @return {Object} Promesa
     */
    getConfig(config, keyName) {
        return this.get(`/config/${keyName}`, config);
    }

    /**
     * Mark notification as read
     * @param {Object} config Axios config
     * @param {Object} body Axios config
     * @param {String} uuidNotification notification uuid
     * @return {Object} Promise
     */
    readNotification(config, uuidNotification) {
        return this.patch(`/notifications/${uuidNotification}`, {}, config);
    }

    getCountries() {
        return this.get('/onboarding/countries-info');
    }

    getProvinces(code) {
        return this.get(`/onboarding/provinces/${code}`);
    }

    getConvenienceTest() {
        return this.get('/onboarding/convenience-test/questions');
    }

    getSubdistributorInfo(uuid) {
        return this.get(`/subdistributors/${uuid}`);
    }

    getAllowTaxDown() {
        return this.get('/tax-down/allow');
    }

    activeTaxDown(payload) {
        return this.post('/tax-down/activate', payload);
    }

    getTaxDownUrl() {
        return this.get('/tax-down/get-url');
    }

    getAllowCobasPlus() {
        return this.get('/cobas-plus/allow');
    }

    activeCobasPlus(payload) {
        return this.post('/cobas-plus/activate', payload);
    }
}

const userService = new UsersService();

export default userService;
